import { type ClipboardEventHandler, type KeyboardEvent } from 'react';
import { createEditor } from 'slate';
import { withHistory } from 'slate-history';
import type { RenderElementProps, RenderLeafProps } from 'slate-react';
import { ReactEditor, Slate, withReact } from 'slate-react';
import type { CustomDescendant, Plugin, PortalRef } from './types';
import * as mention from './components/mention';
import * as pasteHtml from './components/paste-html';
import * as maxLength from './components/max-length';
import * as link from './components/link';
import type { EditableProps } from './scripts/context';
import { editorContext } from './scripts/context';

const plugins = [mention, pasteHtml, maxLength, link] as Plugin[];

const initialValue: CustomDescendant[] = [
  { type: 'paragraph', children: [{ text: '' }] },
];

function renderLeaf({ leaf, attributes, children }: RenderLeafProps) {
  if (leaf) {
    for (const { isLeaf, Leaf } of plugins) {
      if (isLeaf?.(leaf) && Leaf) {
        return (
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          <Leaf leaf={leaf} attributes={attributes}>
            {children}
          </Leaf>
        );
      }
    }
  }
  return <span {...attributes}>{children}</span>;
}

function renderElement({ element, attributes, children }: RenderElementProps) {
  if (element) {
    for (const { isElement, Element } of plugins) {
      if (isElement?.(element) && Element) {
        return (
          <Element element={element} attributes={attributes}>
            {children}
          </Element>
        );
      }
    }
  }
  return <div {...attributes}>{children}</div>;
}

export interface EditorContextProps {
  children: ReactNode;
}

export default function EditorContext({ children }: EditorContextProps) {
  const isComposingRef = useRef(false);
  const editableRef = useRef<HTMLDivElement>(null);

  const editor = useMemo(
    () =>
      plugins.reduce(
        (editor, { withEditor }) => {
          return withEditor ? withEditor(editor) : editor;
        },
        withReact(withHistory(createEditor()))
      ),
    []
  );

  const portalsMap = useMemo(() => new Map<any, PortalRef>(), []);

  const onChange = useCallback(
    (value: CustomDescendant[]) => {
      portalsMap.forEach(({ onChange }) => {
        onChange?.(value);
      });
    },
    [portalsMap]
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (isComposingRef.current) {
        event.stopPropagation();
        event.preventDefault();
        return;
      }

      if (!event.defaultPrevented) {
        portalsMap.forEach(({ onKeyDown }) => {
          onKeyDown?.(event);
        });
      }
    },
    [portalsMap]
  );

  const onPaste = useCallback<ClipboardEventHandler<HTMLDivElement>>(
    (e) => {
      e.preventDefault();
      editor.insertData(e.clipboardData);
    },
    [editor]
  );

  const onCompositionStart = useCallback(() => {
    isComposingRef.current = true;
  }, []);

  const onCompositionEnd = useCallback(() => {
    isComposingRef.current = false;
  }, []);

  const focus = useCallback(() => {
    ReactEditor.focus(editor);
  }, [editor]);

  const clear = useCallback(() => {
    editor.delete({
      at: {
        anchor: editor.start([]),
        focus: editor.end([]),
      },
    });
  }, [editor]);

  const editableProps = useMemo<EditableProps>(
    () => ({
      renderElement,
      renderLeaf,
      onKeyDown,
      onPaste,
      onCompositionStart,
      onCompositionEnd,
      editableRef,
    }),
    [onCompositionEnd, onCompositionStart, onKeyDown, onPaste]
  );

  const contextValue = useMemo(
    () => ({
      editor,
      focus,
      clear,
      editableProps,
    }),
    [clear, editableProps, editor, focus]
  );

  return (
    <editorContext.Provider value={contextValue}>
      <Slate editor={editor} initialValue={initialValue} onChange={onChange}>
        {children}
        {plugins.map((module, i) => {
          const { Portal } = module;
          if (!Portal) {
            return null;
          }

          return (
            <Portal
              key={i}
              ref={(c: any) => {
                if (c) {
                  portalsMap.set(module, c);
                }
              }}
              editableRef={editableRef}
            ></Portal>
          );
        })}
      </Slate>
    </editorContext.Provider>
  );
}
